import { formatPrice } from "@luce/core";
import { getErrorMessage } from "@luce/core/apollo";
import { Box, Card, Icon, Pressable, Skeleton, toast, Typography } from "@luce/ui";

import { useClientViewVouchers } from "../../../billing/gqlhooks";
import { useRemoveVoucher } from "../../gqlhooks/use-remove-voucher";
import { getInvoiceDiscount } from "../../lib";
import type { InvoiceDetailData } from "../../types";

interface InvoiceVoucherBannerProps {
  invoice: InvoiceDetailData;
  /** Opens the host's voucher picker. */
  onOpenVouchers(): void;
}

function VoucherIcon() {
  return (
    <Icon name="SealPercent" size={20} weight="fill" className="text-luce-system-success-default" />
  );
}

export function InvoiceVoucherBanner({ invoice, onOpenVouchers }: InvoiceVoucherBannerProps) {
  const { items, loading } = useClientViewVouchers();
  const count = items.length;
  const { removeVoucher, loading: removing } = useRemoveVoucher(invoice.id);

  const onRemove = async () => {
    try {
      await removeVoucher();
      toast.success("Voucher removed successfully");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to remove voucher"));
    }
  };

  if (loading && !count && !invoice.appliedVoucher) {
    return <Skeleton radius="lg" className="h-12 w-full" />;
  }

  if (invoice.appliedVoucher) {
    return (
      <Card direction="row" gap="sm" layout="compact" className="w-full">
        <VoucherIcon />
        <Pressable
          onClick={onOpenVouchers}
          accessibilityRole="button"
          accessibilityLabel="Change voucher"
          className="gap-spacing-xs flex flex-1 flex-row flex-wrap items-center"
        >
          <Typography variant="label-sm" color="neutral-foreground-default">
            Voucher applied. You saved
          </Typography>
          <Typography variant="label-sm" color="system-success-default">
            {formatPrice(getInvoiceDiscount(invoice), 0)}
          </Typography>
          <Icon name="CaretRight" size={16} className="text-luce-neutral-foreground-default" />
        </Pressable>
        <Pressable
          onClick={onRemove}
          disabled={removing}
          accessibilityRole="button"
          accessibilityLabel="Remove voucher"
          className="px-spacing-xs flex flex-row items-center"
        >
          <Typography variant="label-sm" color="system-danger-default">
            Remove
          </Typography>
        </Pressable>
      </Card>
    );
  }

  if (!count) return null;

  return (
    <Card
      direction="row"
      gap="sm"
      onPress={onOpenVouchers}
      accessibilityLabel="Apply voucher"
      layout="compact"
      className="w-full"
    >
      <VoucherIcon />
      <Typography variant="body-sm" color="neutral-foreground-default" className="flex-1">
        {`You have ${count} vouchers waiting for you!`}
      </Typography>
      <Box className="gap-spacing-xs flex flex-row items-center">
        <Typography variant="label-sm" color="brand-primary" className="hidden md:flex">
          Apply Voucher
        </Typography>
        <Icon name="CaretRight" size={16} className="text-luce-neutral-foreground-default" />
      </Box>
    </Card>
  );
}
